import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import type { CreativeItem } from '@/entities/creatives';

export function ImageCarousel({
  assets,
  resetKey,
}: {
  readonly assets: CreativeItem['assets'];
  readonly resetKey: unknown;
}) {
  const images = assets.filter(
    (a): a is typeof a & { url: string } => a.url !== null,
  );
  const [index, setIndex] = useState(0);
  const hasMultipleImages = images.length > 1;

  useEffect(() => {
    setIndex(0);
  }, [resetKey]);

  if (images.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No images available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {hasMultipleImages && (
          <IconButton
            size="small"
            onClick={() => setIndex((i) => i - 1)}
            disabled={index === 0}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
        <Box
          component="img"
          src={images[index].url}
          sx={{
            flex: 1,
            minWidth: 0,
            height: 400,
            display: 'block',
            objectFit: 'contain',
            borderRadius: 1,
          }}
        />
        {hasMultipleImages && (
          <IconButton
            size="small"
            onClick={() => setIndex((i) => i + 1)}
            disabled={index === images.length - 1}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>

      {hasMultipleImages && (
        <>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 0.5,
              color: 'text.secondary',
            }}
          >
            {index + 1} / {images.length}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              justifyContent: 'center',
              mt: 1,
              flexWrap: 'wrap',
            }}
          >
            {images.map((img, i) => (
              <Box
                key={img.id}
                component="img"
                src={img.url}
                onClick={() => setIndex(i)}
                sx={{
                  width: 40,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  opacity: i === index ? 1 : 0.45,
                  border: i === index ? '2px solid' : '2px solid transparent',
                  borderColor: i === index ? 'primary.main' : 'transparent',
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
